import { toast } from 'sonner'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TabsContent } from '@/components/ui/tabs'
import { updateWallState, type WallWinner } from '@/lib/firebase/activities'

interface Props {
  winners: WallWinner[]
  currentSessionId: string
  allWinners: WallWinner[]
}

export function WallWinnersTab({
  winners,
  currentSessionId,
  allWinners,
}: Props) {
  const handleResetWinners = async () => {
    if (
      !confirm(
        'คุณต้องการล้างรายชื่อผู้ชนะทั้งหมดในเซสชันปัจจุบันนี้ใช่หรือไม่? (การกระทำนี้จะลบเฉพาะผู้ชนะในเซสชันนี้เท่านั้น)',
      )
    ) {
      return
    }
    const filteredWinners = allWinners.filter(
      (w): w is WallWinner =>
        w !== null && (w.sessionId || 'default') !== currentSessionId,
    )
    try {
      await updateWallState({ winners: filteredWinners, winner: null })
      toast.info('ล้างรายชื่อผู้ชนะในเซสชันนี้แล้ว')
    } catch (err) {
      console.error(err)
      toast.error('เกิดข้อผิดพลาดในการล้างรายชื่อ')
    }
  }
  return (
    <TabsContent value="winners">
      <Card className="px-4 py-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-bold">รายชื่อผู้ชนะ</CardTitle>
            <CardDescription>
              ผู้ชนะในเซสชันปัจจุบัน ({winners.length} คน)
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetWinners}
            className="text-destructive border-destructive/20 hover:bg-destructive/5 rounded-xl font-bold"
          >
            ล้างรายชื่อ
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ผู้ชนะ</TableHead>
                <TableHead className="text-right">เวลา</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {winners.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="text-muted-foreground py-10 text-center"
                  >
                    ยังไม่มีผู้ชนะ
                  </TableCell>
                </TableRow>
              ) : (
                [...winners].reverse().map((w, i) => (
                  <TableRow key={`${w?.id || 'winner'}-${i}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="size-9 border border-rose-100">
                          <AvatarImage src={w?.pictureUrl || undefined} />
                          <AvatarFallback className="bg-rose-50 text-xs text-rose-500 uppercase">
                            {w?.name ? w.name.slice(0, 2) : 'WN'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-bold">
                          {w?.name || 'Unknown'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-right text-sm">
                      {w?.timestamp
                        ? new Date(w.timestamp).toLocaleTimeString('th-TH', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          })
                        : ''}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </TabsContent>
  )
}
